import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAuthSession, getCurrentUser, signOut, signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isIOS, isNative } from '../utils/platform';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      const claims = idToken?.payload || {};
      // Apple Sign In users get a generated apple_*@apple.drift.id "email"
      // and store their real address in preferred_username. Prefer that.
      const realEmail = claims.preferred_username || claims.email || '';
      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email: realEmail,
        name: claims.name || '',
        token: idToken?.toString() || '',
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkUser();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'tokenRefresh':
          checkUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, [checkUser]);

  const loginWithGoogle = useCallback(async () => {
    await signInWithRedirect({ provider: 'Google' });
  }, []);

  const loginWithApple = useCallback(async () => {
    if (isNative() && isIOS()) {
      const { nativeAppleSignIn } = await import('../services/nativeAppleSignIn');
      await nativeAppleSignIn();
      // Token injection bypasses Hub events; force a refresh.
      await checkUser();
      return;
    }
    // v6 federation provider format: provider.custom for SignInWithApple
    await signInWithRedirect({ provider: { custom: 'SignInWithApple' } });
  }, [checkUser]);

  const logout = useCallback(async () => {
    await signOut();
    setUser(null);
  }, []);

  const getAuthToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.idToken?.toString() || '';
    } catch {
      return '';
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      loginWithGoogle,
      loginWithApple,
      logout,
      getAuthToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
